import {
  NavigationProp,
  ParamListBase,
  RouteProp,
} from "@react-navigation/native";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Dimensions,
} from "react-native";
import ViewingEntity from "../components/common/ViewingEntity";
import { EvilIcons, MaterialCommunityIcons, Entypo } from "@expo/vector-icons";
import { Colors, Spacing, Typography } from "../styles";
import VisibilityData from "../components/common/VisibilityData";
import Graph from "../components/common/Graph";
import React, { useState } from "react";
import Times, { TimeOption } from "../components/common/Times";
import Button from "../components/common/Button";
import ProIcon from "../components/common/ProIcon";
import Slider from "@react-native-community/slider";
import Task from "../components/today/Task";
import { BlurView } from "expo-blur";
import Observation from "../components/common/Observation";
import { Title as ObservationsTitle } from "./Observations";
import { timestampToRelativeTime } from "../utils";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEntity,
  selectInsights,
  selectInsightWithEntity,
  selectInWatchlist,
  setVisibilityTarget,
  toggleWatchlistID,
} from "../redux/mainSlice";
import { InsightID } from "../types/data";
import { RootState } from "../redux";
import { RootStackParamList } from "../navigators/StackNavigator";
import Card from "../components/common/Card";
import { nanoid } from "@reduxjs/toolkit";
import { task } from "../utils/genData";

const Insight = ({
  navigation,
  route,
}: {
  navigation: NavigationProp<RootStackParamList>;
  route: RouteProp<ParamListBase, string>;
}) => {
  const params = route.params as any;
  const id = params.id as InsightID;

  //#region Redux
  const dispatch = useDispatch();

  const entity = useSelector(selectEntity);

  const insight = useSelector((state: RootState) =>
    selectInsightWithEntity(state, id)
  );

  const inWatchlist = useSelector((state: RootState) =>
    selectInWatchlist(state, id)
  );

  const target = insight?.entityVisibility.target;

  let tasks = target
    ? insight.insight.tasks.filter((t) => t.threshold >= target)
    : insight?.insight.tasks;
  tasks = tasks?.slice(0, 3);
  //#endregion

  const [width, setWidth] = useState<number>(0);
  const [selectedTimeOption, setSelectedTimeOption] =
    useState<TimeOption>("2m");

  if (!insight) return <></>;

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      <View style={styles.inner}>
        <ViewingEntity goBack />

        {/* Main card */}
        <View
          style={[styles.card, styles.sectionMargin]}
          onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
        >
          {/* Header */}
          <View style={[styles.row, styles.sectionMargin]}>
            <Text style={[Typography.paragraph, { opacity: 0.5 }]}>
              {timestampToRelativeTime(insight.insight.date)}
            </Text>
            <View style={styles.row}>
              <EvilIcons
                name="share-apple"
                size={24}
                color={Colors.white}
                style={{ marginRight: 10 }}
              />
              <TouchableOpacity
                onPress={() =>
                  navigation.navigate("Compare", {
                    id: insight.insight.id,
                  })
                }
              >
                <MaterialCommunityIcons
                  name="weight"
                  size={18}
                  color={Colors.white}
                />
              </TouchableOpacity>
            </View>
          </View>

          <Text style={Typography.cardTitle}>{insight.insight.title}</Text>
          <VisibilityData
            entity={entity}
            visibility={insight.entityVisibility}
            style={styles.visibilityData}
          />
          <Graph
            data={insight.entityVisibility.data}
            style={styles.itemMargin}
            timeOption={selectedTimeOption}
          />
          <Times
            selected={selectedTimeOption}
            setSelected={setSelectedTimeOption}
            style={styles.itemMargin}
          />
          <Button
            title={inWatchlist ? "Remove from watchlist" : "Add to watchlist"}
            onPress={() => dispatch(toggleWatchlistID(id))}
            style={styles.sectionMargin}
            // borderColor={Colors.teal700}
          />

          {/* Visibility slider */}
          <View style={styles.sectionMargin}>
            <View style={[styles.row, styles.itemMargin]}>
              <View style={styles.row}>
                <Text style={Typography.title}>Target visibility</Text>
                <Text
                  style={[
                    Typography.paragraph,
                    { opacity: 0.5, marginLeft: 5 },
                  ]}
                >
                  {insight.entityVisibility.target !== undefined
                    ? insight.entityVisibility.target + "%"
                    : "-"}
                </Text>
              </View>
              {/* <ProIcon /> */}
            </View>
            <Slider
              style={{ width: "100%", height: 40 }}
              minimumValue={0}
              maximumValue={100}
              minimumTrackTintColor="#FFF"
              maximumTrackTintColor="#777"
              step={25}
              thumbTintColor={Colors.white}
              value={insight.entityVisibility.target}
              // value={visibilityTargetLocal}
              onSlidingComplete={(val) => {
                // setVisibilityTargetLocal(val);
                dispatch(setVisibilityTarget({ id, target: val }));
              }}
              // onValueChange={(val) => setVisibilityTargetLocal(val)}
              // onValueChange={(val) =>
              //   dispatch(setVisibilityTarget({ id, target: val }))
              // }
              tapToSeek={true}
            />
            <View style={[styles.row, styles.sliderTicks]}>
              <View style={styles.sliderTick} />
              <View style={styles.sliderTick} />
              <View style={styles.sliderTick} />
              <View style={styles.sliderTick} />
              <View style={styles.sliderTick} />
            </View>
            <View style={styles.row}>
              <Text
                style={[Typography.paragraph, { fontSize: 8, opacity: 0.5 }]}
              >
                Minimal
              </Text>
              <Text
                style={[Typography.paragraph, { fontSize: 8, opacity: 0.5 }]}
              >
                Slightly
              </Text>
              <Text
                style={[Typography.paragraph, { fontSize: 8, opacity: 0.5 }]}
              >
                Somewhat
              </Text>
              <Text
                style={[Typography.paragraph, { fontSize: 8, opacity: 0.5 }]}
              >
                Quite
              </Text>
              <Text
                style={[Typography.paragraph, { fontSize: 8, opacity: 0.5 }]}
              >
                Highly
              </Text>
            </View>
          </View>

          {/* Tasks */}
          <View style={styles.sectionMargin}>
            <Text style={[Typography.title, styles.itemMargin]}>Tasks</Text>
            <View style={styles.tasks}>
              {/* Tasks */}
              <View>
                {tasks && tasks.length > 0 ? (
                  tasks.map((t) => (
                    <Task
                      entity={t.entity}
                      text={t.text}
                      style={{ marginBottom: 10 }}
                      key={nanoid()}
                    />
                  ))
                ) : (
                  <View style={{ alignItems: "center" }}>
                    <Text style={[Typography.paragraph, { opacity: 0.5 }]}>
                      None
                    </Text>
                  </View>
                )}
              </View>
              {/* Text */}
              {/* <BlurView
                style={[styles.lockedTasks]}
                intensity={80}
                tint={"dark"}
              >
                <View style={styles.row}>
                  <Text style={Typography.paragraph}>Go </Text>
                  <ProIcon />
                  <Text style={Typography.paragraph}>to unlock</Text>
                </View>
              </BlurView> */}
            </View>
          </View>

          {/* Observations */}
          <View>
            <ObservationsTitle />
            {insight.insight.observations.slice(0, 3).map((o) => (
              <Observation {...o} style={{ marginBottom: 10 }} />
            ))}
            <TouchableOpacity
              onPress={() =>
                navigation.navigate("Observations", { id: insight.insight.id })
              }
            >
              <View
                style={{
                  flexDirection: "row",
                  opacity: 0.5,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text style={[Typography.paragraph]}>See all</Text>
                <Entypo
                  name="chevron-small-right"
                  size={16}
                  color={Colors.white}
                  style={{ marginTop: 2 }}
                />
              </View>
            </TouchableOpacity>
          </View>
        </View>

        {/* Related stories */}
        {insight.insight.relatedInsights.length !== 0 && (
          <>
            <Text style={[Typography.title, styles.itemMargin]}>
              Related stories
            </Text>
            {insight.insight.relatedInsights.map((id) => (
              <Card id={id} style={{ marginBottom: 20 }} key={nanoid()} />
            ))}
          </>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    // flex: 1,
    backgroundColor: Colors.plum900,
    height: Dimensions.get("window").height,
    // borderWidth: 3,
    // borderColor: "#f0f",
  },
  inner: {
    padding: Spacing.page,
  },
  card: {
    backgroundColor: Colors.plum700,
    borderRadius: Spacing.cardRadius,
    padding: Spacing.card,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sectionMargin: {
    marginBottom: 50,
  },
  itemMargin: {
    marginBottom: 10,
  },
  visibilityData: {
    marginVertical: 10,
  },
  sliderTicks: { marginHorizontal: 10, marginTop: -20, zIndex: -1 },
  sliderTick: {
    width: 1,
    height: 12,
    backgroundColor: Colors.plum400,
  },
  tasks: {
    position: "relative",
  },
  lockedTasks: {
    width: "100%",
    height: "100%",
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
  },
});

export default Insight;
